/**
 * Recent-tickets stack — the "Recent" section of the ⌘K palette.
 *
 * Persists to localStorage so jumping back to a ticket you opened
 * yesterday is one keystroke. Capped at RECENT_LIMIT entries.
 *
 * Single global subscriber model — components that mount the
 * `recordRecentTicket` call (e.g. TicketDetail on mount) push to
 * the stack; the palette reads via `useRecentTickets()` and
 * re-renders when the stack changes.
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'beacon-recent-tickets';
const RECENT_LIMIT = 8;

export interface RecentTicketEntry {
  number: string;
  subject: string;
  contractName: string;
  /** ISO timestamp — last visit. Most recent first in the returned list. */
  visitedAt: string;
}

const subscribers = new Set<() => void>();

function read(): RecentTicketEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as RecentTicketEntry[];
  } catch {
    return [];
  }
}

function write(entries: RecentTicketEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota exceeded or storage disabled — soft-fail.
  }
  subscribers.forEach((fn) => fn());
}

/** Push a ticket onto the front of the recent stack. Dedupes by number. */
export function recordRecentTicket(entry: Omit<RecentTicketEntry, 'visitedAt'>): void {
  const existing = read().filter((e) => e.number !== entry.number);
  const next: RecentTicketEntry[] = [
    { ...entry, visitedAt: new Date().toISOString() },
    ...existing,
  ].slice(0, RECENT_LIMIT);
  write(next);
}

/** Subscription hook for the palette. Returns the live stack. */
export function useRecentTickets(): RecentTicketEntry[] {
  const [entries, setEntries] = useState<RecentTicketEntry[]>(() => read());
  useEffect(() => {
    const update = () => setEntries(read());
    subscribers.add(update);
    return () => {
      subscribers.delete(update);
    };
  }, []);
  return entries;
}
